import React, { ReactNode, useContext, useState } from "react";
import { MainNav } from "@/components/custom/main-nav";
import { UserNav } from "@/components/custom/user-nav";
import { Toaster } from "@/components/ui/toaster";
import { InfoRequest } from "@/components/custom/InfoRequest/InfoRequest";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { Button } from "@/components/ui/button";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import { useCustomBranding } from "@/hooks/useSiteSettings";
import { useTranslation } from "react-i18next";
import { Badge } from "../ui/badge";
import { useITAdminPermission } from "@/hooks/usePermission";

function Header() {
  const siteBranding = useCustomBranding();
  const { t } = useTranslation();
  const isItAdmin = useITAdminPermission();

  return (
    <div className="w-full border-b pb-2 md:pb-0">
      <div className="w-full h-18 md:h-16 px-4 flex flex-wrap flex-col md:grid md:grid-cols-3 gap-4 items-center">
        <Link
          to="/"
          className="flex justify-center items-center md:justify-start items-center w-full md:w-auto text-center md:text-left my-2 md:my-0"
        >
          {siteBranding.logo ? (
            <>
              <img
                src={siteBranding.logo.toString()}
                className="h-12 w-auto"
                alt={siteBranding.title}
              />
            </>
          ) : (
            <img
              src={siteBranding.co_branding_logo.toString()}
              className="h-12 w-auto"
              alt={siteBranding.title}
            />
          )}
        </Link>
        <div className="flex items-center justify-center">
          {siteBranding.logo && (
            <a
              href="https://www.mindsetintegrated.com"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={siteBranding.co_branding_logo.toString()}
                className="h-12 w-auto"
                alt={siteBranding.title}
              />
            </a>
          )}
        </div>
        <div className="flex justify-end items-center text-center md:text-left">
          {isItAdmin ? (
            <Badge className="mx-4">{t("common.statictext.it_admin")}</Badge>
          ) : (
            <MainNav className="mx-2 md:mx-6" />
          )}
          <UserNav />
        </div>
      </div>
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;
  const { user } = contextValues;
  const [openRequestInfo, setOpenRequestInfo] = useState<boolean>(false);
  const isItAdmin = useITAdminPermission();

  return (
    <div className="border-t flex flex-wrap-reverse justify-center gap-2 md:gap-0 md:justify-between items-center px-4 py-2 w-full">
      <div className="flex flex-col text-center gap-2 h-16 items-center md:px-4 md:items-start">
        <span>
          {t("layout.statictext.copyright", {
            currentYear: new Date().getFullYear(),
          })}
        </span>
        <span>
          <a
            className="text-primary underline"
            href="/eula.html"
            target="_blank"
            referrerPolicy="no-referrer"
          >
            {t("common.statictext.eula")}
          </a>
        </span>
      </div>
      {!!user?.user.email && !isItAdmin && (
        <InfoRequest
          open={openRequestInfo}
          setOpen={setOpenRequestInfo}
          trigger={
            <Button variant="link" className="flex gap-1">
              <BsFillInfoCircleFill /> {t("layout.button.request_info")}
            </Button>
          }
        />
      )}
    </div>
  );
}

function Content({ children }: { children: ReactNode }) {
  return <div className="mb-auto px-4 py-8 min-h-screen">{children}</div>;
}

export default function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <ScrollToTop />
      <Toaster />
      <div className="flex-col min-h-screen justify-between">
        <Header />
        <Content>{children}</Content>
        <Footer />
      </div>
    </>
  );
}
